import React, { useEffect, useState } from 'react'

import {
  Card,
  ChevronUpIcon,
  CloseIcon,
  Col,
  IconButton,
  Row,
  Button,
  ArrowLeftIcon,
  Badge,
  TextDisplay,
} from '@paypalcorp/pp-react'
import { Typography } from '@paypalcorp/ppods-react'
import { Message, injectWorldReady } from '@paypalcorp/worldready-react'
import { useApplicationContext } from '../../../context/ApplicationContext'
import { recordDynamicEvent } from '../../../components/common/analytics/utils'
import { saveBannerProgress } from '../../../utils/session-utils'
import { getUserType } from '../../../utils/user-utils'
import apiFactory from '../../../utils/api-factory'
import MinimizedBanner from './minimized-banner'
import BannerContent from './banner-content'

const OnboardingBanner = ({
  closeBanner,
  bannerPage,
  setBannerPage,
  productIntegration,
  setProductIntegration,
  setIsSavingUserPreferences,
}) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [isCredsForClient, setIsCredsForClient] = useState(false)

  const {
    logger,
    csrf,
    user,
    setReloadPage,
    setCurrentEnvironment,
    enviroment: environment,
    liveApps,
    setCurrentBannerStep,
  } = useApplicationContext()

  useEffect(() => {
    if (environment === 'live' && bannerPage === 2) {
      setBannerPage(4)
      setReloadPage('yes')
    }

    if (environment === 'sandbox' && bannerPage === 4) {
      setBannerPage(2)
      setReloadPage('yes')
    }
  }, [environment])

  const accountType = getUserType(user)
  const liveApp = liveApps?.[0]

  const DEFAULT_BANNER_TITLE = 'onboarding-banner.paymentSolutionTitle'

  const bannerTitle4Page = () => {
    if (isCredsForClient) {
      return 'onboarding-banner.liveCredentialsTitle'
    }

    if (accountType === 'BUSINESS' && !liveApp) {
      return 'onboarding-banner.createLiveAppTitle'
    }

    return 'onboarding-banner.livePaymentsTitle'
  }

  const BANNER_TITLES = {
    1: `onboarding-banner.product.${productIntegration}.title`,
    2: 'onboarding-banner.sandboxCredentialsTitle',
    3: 'onboarding-banner.sandboxPaymentsTitle',
    4: bannerTitle4Page(),
    5: 'onboarding-banner.doneTestingTitle',
  }

  const clickHandler = () => {
    if (isMinimized) {
      setIsMinimized(false)
    } else {
      setIsMinimized(true)
    }
  }

  const addUserPreference = async () => {
    setIsSavingUserPreferences(true)
    const userBannerPreference = {
      preferenceName: 'ONBOARDING_BANNER',
      preferenceValue: 'CLOSED',
    }

    await apiFactory.addUserPreference(csrf, userBannerPreference)
    setIsSavingUserPreferences(false)
  }

  const closeHandler = () => {
    closeBanner(true)
    recordDynamicEvent(`onboarding_banner_close,click,${bannerPage}`)

    const sessionObject = {
      bannerPage,
      productIntegration,
      closeOnboardingBanner: true,
    }

    setCurrentBannerStep(sessionObject)
    saveBannerProgress(csrf, logger, sessionObject)
    addUserPreference()
  }

  const goBackHandler = () => {
    let targetBannerPage = bannerPage - 1
    if (bannerPage === 2) {
      targetBannerPage = 0
    }
    const sessionObject = {
      bannerPage: targetBannerPage,
      productIntegration,
    }
    setCurrentBannerStep(sessionObject)
    saveBannerProgress(csrf, logger, sessionObject)
    setBannerPage(targetBannerPage)
    if (bannerPage === 4 && environment === 'live') {
      setCurrentEnvironment('sandbox')
      setReloadPage('yes')
    }
    recordDynamicEvent(
      `onboarding_banner_go_back,click,goback,step${bannerPage}`,
    )
  }

  if (isMinimized) {
    return (
      <MinimizedBanner
        closeBanner={closeBanner}
        bannerPage={bannerPage}
        bannerTitle={BANNER_TITLES[bannerPage] || DEFAULT_BANNER_TITLE}
        isCredsForClient={isCredsForClient}
        productIntegration={productIntegration}
        setIsSavingUserPreferences={setIsSavingUserPreferences}
        isMinimized={isMinimized}
        setIsMinimized={setIsMinimized}
      />
    )
  }

  return (
    <Card>
      <Row className="banner-header">
        {bannerPage > 0 && bannerPage < 5 ? (
          <>
            <Col xs={2}>
              <IconButton
                size="xs"
                onClick={goBackHandler}
                buttonType="tertiary"
                svgIconComponent={ArrowLeftIcon}
              />
              <Button
                tertiary
                size="xs"
                onClick={goBackHandler}
                style={{ top: '-5px' }}
              >
                <Message id="onboarding-banner.goBack" />
              </Button>
            </Col>

            <Col xs={6} />
            <Col xs={2} align="right">
              <Badge className="banner-badge" type="neutral">
                <Message
                  id="onboarding-banner.stepBadge"
                  number={bannerPage}
                  total="4"
                />
              </Badge>
            </Col>
          </>
        ) : (
          <Col xs={10}>
            <TextDisplay
              className="banner-title"
              name="banner-title"
              label=""
              value={
                <Typography variant="title-lg" style={{ margin: 0 }}>
                  <strong>
                    {' '}
                    <Message
                      id={BANNER_TITLES[bannerPage] || DEFAULT_BANNER_TITLE}
                    />
                  </strong>
                </Typography>
              }
            />
          </Col>
        )}
        <Col align="right">
          {bannerPage !== 5 && (
            <IconButton
              size="sm"
              onClick={clickHandler}
              buttonType="tertiary"
              svgIconComponent={ChevronUpIcon}
            />
          )}
          <IconButton
            size="sm"
            onClick={closeHandler}
            buttonType="tertiary"
            svgIconComponent={CloseIcon}
            style={{
              marginLeft: '12px',
              marginRight: '12px',
            }}
          />
        </Col>
      </Row>
      <BannerContent
        accountType={accountType}
        bannerPage={bannerPage}
        isCredsForClient={isCredsForClient}
        setBannerPage={setBannerPage}
        productIntegration={productIntegration}
        setIsCredsForClient={setIsCredsForClient}
        setProductIntegration={setProductIntegration}
      />
    </Card>
  )
}

export default injectWorldReady(OnboardingBanner)
